import type React from 'react';
import {
    type InputHTMLAttributes,
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useRef,
    type KeyboardEvent
} from 'react';
import styled, { css, type RuleSet } from 'styled-components';
import { colors } from 'variables';
import { rem } from 'modules/helpers/style';
import { Typography } from 'components/generic/typography';
import successIcon from '../../../images/icons_input-success.svg';
import errorIcon from '../../../images/icons_input-error.svg';
import InputToggle from './inputToggle';

export interface IValidation {
    message?: string;
    state?: boolean;
}

export const Input = styled.input<{ validationState?: boolean }>`
    position: relative;
    height: ${rem('40px')};
    width: 100%;
    padding: 0 40px 0 10px;
    outline: none;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: calc(100% - 10px) center;
    background-size: 15px;
    border: 1px solid ${colors.quinary};

    margin-bottom: 0;

    ${({ validationState }): RuleSet | null => {
        switch (validationState) {
            case true:
                return css`
                    border-color: ${colors.success};
                    background-color: ${colors.successLight};
                `;
            case false:
                return css`
                    border-color: ${colors.error};
                    background-color: ${colors.errorLight};
                `;
            default:
                return null;
        }
    }}
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const InputContainer = styled.div<{ validationState?: boolean }>`
    /* For validation icon */
    position: relative;

    &:after {
        ${({ validationState }): RuleSet =>
            typeof validationState === 'boolean' &&
            css`
                content: url(${validationState ? successIcon : errorIcon});
                position: absolute;
                height: ${rem(38)};
                width: ${rem(38)};
                padding: ${rem(10)};
                right: 1px;
                transform: translateY(-50%);
                top: 50%;
            `}
    }
`;

export const Title = styled(Typography.Body)<{ validationState?: boolean }>`
    margin-bottom: 6px;
    ${({ validationState }): string =>
        typeof validationState === 'boolean' && `color: ${validationState ? colors.success : colors.error};`}
`;

const Notification = styled.span`
    font-style: italic;
    font-size: 11px;
    align-self: flex-end;
    color: ${colors.positive};
`;

export interface IDefaultInput extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    placeholder?: string;
    onValueChange?: (value: InputHTMLAttributes<HTMLInputElement>['value']) => void;
    onEnter?: (value) => void;
    validation?: IValidation;
    notification?: string;
    labelComponent?: () => JSX.Element;
    isPassword?: boolean;
}

export default forwardRef<HTMLInputElement, IDefaultInput>((props: IDefaultInput, ref): JSX.Element => {
    const {
        label,
        placeholder,
        notification,
        value: initialValue = '',
        onValueChange,
        onChange: initialOnChange,
        onEnter,
        labelComponent,
        isPassword = false,
        validation = {},
        ...rest
    } = props;
    const [value, setValue] = useState(initialValue || '');
    const { state, message } = validation;

    const inputRef = useRef(null);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setValue((e.target as HTMLInputElement).value);
        if (initialOnChange) initialOnChange(e);
    };

    const onKeyup = (event: KeyboardEvent<HTMLInputElement>) => {
        const { key } = event;

        if (key === 'Enter' && onEnter) {
            onEnter(event);
        }
    };

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect((): void => {
        if (onValueChange) onValueChange(value);
    }, [onValueChange, value]);

    const setInputValue = (val): void => {
        if (typeof val === 'undefined' || val === null) {
            return;
        }
        setValue(val);
    };

    useImperativeHandle(ref, (): any => ({ setInputValue }));

    const GetTitle = (): JSX.Element | null => {
        if (validation && message) {
            return (
                <Title size="M" strong validationState={state} forwardedAs="span">
                    {message}
                </Title>
            );
        }
        if (labelComponent) {
            return labelComponent();
        }
        if (label) {
            return (
                <Title size="M" strong forwardedAs="span">
                    {label}
                </Title>
            );
        }
        return null;
    };

    return (
        <Wrapper>
            <GetTitle />
            <InputContainer validationState={state}>
                <Input
                    value={value}
                    onChange={onChange}
                    onKeyUp={onKeyup}
                    ref={inputRef}
                    placeholder={placeholder}
                    validationState={state}
                    {...rest}
                />
                {isPassword && <InputToggle inputRef={inputRef} validationState={state} />}
            </InputContainer>
            {notification && <Notification>{notification}</Notification>}
        </Wrapper>
    );
});
